// src/lib/checklists.ts — move-in / move-out checklists and changeovers (Task F): shapes and calls
// shared by the tenancy page's checklist panels, the Changeovers page and the property page.
import { api } from './api';

export type ChecklistKind = 'MOVE_IN' | 'MOVE_OUT';
export type ItemEffect = 'ACTIVATE_TENANCY' | 'END_TENANCY' | 'BOOK_INSPECTION' | 'CLEANING_WORK_ORDER' | 'WORKS_WORK_ORDER' | 'DEPOSIT_STATUS' | 'EJARI_NUMBER' | 'ALLOCATE_PARKING' | 'CONFIRM_ID_GOODLORD';

export interface FieldDef { key: string; label: string; type: 'text' | 'number' | 'date' | 'select'; options?: string[] }
export interface ChecklistItem {
  key: string; label: string; hint?: string; effect?: ItemEffect; fields?: FieldDef[]; optional?: boolean;
  /** The item takes a document (the photo of the ID): stored with the application's documents; `required` → none, no tick. */
  upload?: { category: string; label?: string; required?: boolean };
  /** What Goodlord said when the item was ticked (CONFIRM_ID_GOODLORD). */
  effectResult?: { sent: boolean; ok: boolean; text: string; at: string } | null;
  done: boolean; na: boolean; doneBy: string | null; doneByName: string | null; doneAt: string | null;
  value: Record<string, string>; note: string | null; ref: { type: string; id: string; label: string } | null;
}
export interface ChecklistStage { key: string; label: string; items: ChecklistItem[]; done: number; total: number }
export interface Checklist {
  id: string; tenancyId: string; unitId: string; propertyId: string; kind: ChecklistKind; region: string; templateKey: string;
  status: 'OPEN' | 'COMPLETE'; targetDate: string | null; completedAt: string | null; completedByName: string | null;
  stages: ChecklistStage[]; progress: { done: number; total: number; percent: number };
  outstanding: number; allDone: boolean; overdue: boolean; daysToTarget: number | null;
  nextItem: { key: string; label: string; stage: string } | null;
  tenancy: { id: string; reference: string; status: string; startDate: string | null; endDate: string | null; moveInDate: string | null; moveOutDate: string | null; unitReference: string; propertyName: string; tenants: string; inviteCode: string | null; invitedAt: string | null; inviteExpiresAt: string | null };
  deposit: { id: string; status: string; amountMinor: number; currency: string; scheme: string | null; schemeReference: string | null; protectedOn: string | null; protectionDueBy: string | null; isOverdue: boolean } | null;
  welcome: Array<{ id: string; status: string; toEmail: string | null; toName: string | null; sentAt: string | null; error: string | null }>;
  updatedAt: string;
}
export interface TickBody { done?: boolean; na?: boolean; value?: Record<string, string>; note?: string | null }

export interface ClosingDueRow {
  checklistId: string; tenancyId: string; reference: string; unitReference: string; propertyId: string; propertyName: string;
  tenants: string; moveInDate: string | null; completedAt: string; inviteCode: string | null;
  letters: Array<{ id: string; status: string; toEmail: string | null; toName: string | null; error: string | null }>; canSend: boolean;
}

export const PHASES = ['NOTICE_GIVEN', 'ON_MARKET', 'OFFER_ACCEPTED', 'KEYS_BACK', 'CLEANING_WORKS', 'READY', 'MOVED_IN'] as const;
export type Phase = (typeof PHASES)[number];
/** Phase label and colour (prototype CO_PHASE). */
export const PHASE_INFO: Record<Phase, { label: string; colour: string; hint: string }> = {
  NOTICE_GIVEN: { label: 'Notice given', colour: '#d4932c', hint: 'Notice is in and it is not advertised yet.' },
  ON_MARKET: { label: 'On the market', colour: '#2f9e6e', hint: 'Still occupied, advertised, taking viewings.' },
  OFFER_ACCEPTED: { label: 'Let agreed', colour: '#4f7fd6', hint: 'The next tenant is lined up.' },
  KEYS_BACK: { label: 'Keys back', colour: '#8b6fd6', hint: 'Empty — nothing booked to put it right yet.' },
  CLEANING_WORKS: { label: 'Cleaning / works', colour: '#a06cd5', hint: 'Empty while it is put back together.' },
  READY: { label: 'Ready to re-let', colour: '#2bb87c', hint: 'Done and waiting for the next tenant.' },
  MOVED_IN: { label: 'New tenant in', colour: '#6b7f95', hint: 'The next tenancy has started.' },
};

export interface Changeover {
  unitId: string; unitReference: string; unitName: string | null; bedrooms: number | null; propertyId: string; propertyName: string; region: string;
  phase: Phase; phaseLabel: string; steps: Array<{ key: string; label: string; done: boolean }>; nDone: number; next: string | null;
  outgoing: { tenancyId: string; reference: string; tenants: string; status: string; endDate: string | null; noticeGivenOn: string | null };
  incoming: { tenancyId: string; reference: string; tenants: string; status: string; startDate: string | null } | null;
  application: { id: string; reference: string; applicant: string | null; status: string; requestedMoveIn: string | null } | null;
  offer: { id: string; applicant: string | null; agreedMinor: number; startDate: string | null; acceptedAt: string | null; applicationId: string | null } | null;
  listing: { id: string; status: string; advertisedRentMinor: number | null; availableFrom: string | null } | null;
  moveOut: { checklistId: string; status: string; outstanding: number; total: number; keysReturned: boolean; checkoutDone: boolean } | null;
  moveIn: { checklistId: string; status: string; outstanding: number; total: number } | null;
  openWorkOrders: number; voidDays: number; projectedVoidDays: number | null; askMinor: number; currency: string; lostMinor: number;
  targetReletDate: string | null; targetPassed: boolean; note: string | null;
}
export interface ChangeoverCounts { total: number; byPhase: Record<Phase, number>; vacating: number; empty: number; voidDays: number; lostMinor: number; targetPassed: number }

/** One card on the development's Moving in & out tab (GET /properties/:id/moving). */
interface MovingCardBase {
  checklistId: string; tenancyId: string; tenancyReference: string; tenancyStatus: string;
  unitId: string; unitReference: string; tenantName: string;
  done: number; total: number; percent: number; outstanding: string[];
  status: 'OPEN' | 'COMPLETE'; completedAt: string | null; daysToDate: number | null; overdue: boolean;
}
export interface MovingInCard extends MovingCardBase { startDate: string | null; welcomeSent: boolean; welcomeDue: boolean }
export interface MovingOutCard extends MovingCardBase { endDate: string | null; closingSent: null }
export interface MovingChangeover extends Changeover {
  lastTenancyEnds: string | null; nextTenancyStarts: string | null; daysEmpty: number; rentNotEarnedMinor: number;
  targetBackOnMarket: string | null; notes: string | null; total: number;
}
export interface PropertyMoving {
  propertyId: string;
  tiles: { movingIn: number | null; movingOut: number | null; changeovers: number | null; vacatingOnMarket: number | null; daysEmptyTotal: number | null; rentNotEarnedMinor: number | null; currency: string };
  movingIn: MovingInCard[] | null; movingOut: MovingOutCard[] | null; changeovers: MovingChangeover[] | null;
}

/** A checklist template as GET /checklists/templates lists it (checklists.service listTemplates): its stages, items unticked. */
export interface ChecklistTemplate {
  key: string; kind: ChecklistKind; region: string | null; name: string; isSystem: boolean; items: number;
  stages: Array<{ key: string; label: string; items: Array<{ key: string; label: string; hint?: string; optional?: boolean }> }>;
}

export const checklistsApi = {
  templates: async () => (await api.get<ChecklistTemplate[]>('/checklists/templates')).data,
  moving: async (propertyId: string) => (await api.get<PropertyMoving>(`/properties/${propertyId}/moving`)).data,
  get: async (tenancyId: string, kind: ChecklistKind) => (await api.get<Checklist | null>(`/checklists/${tenancyId}/${kind}`)).data,
  tick: async (id: string, key: string, body: TickBody) => (await api.post<Checklist>(`/checklists/${id}/items/${key}`, body)).data,
  attachDocument: async (id: string, key: string, file: { fileName: string; contentType: string; dataBase64: string }) => (await api.post<Checklist>(`/checklists/${id}/items/${key}/document`, file)).data,
  complete: async (id: string) => (await api.post<{ checklist: Checklist; letters: Array<{ id: string; status: string; toEmail: string | null }>; warning: string | null }>(`/checklists/${id}/complete`, {})).data,
  sendWelcome: async (id: string) => (await api.post<{ sent: number; results: Array<{ id: string; toEmail: string | null; status: string; error: string | null }> }>(`/checklists/${id}/send-welcome`, {})).data,
  closingDue: async (propertyId?: string) => (await api.get<ClosingDueRow[]>('/checklists/closing-due', propertyId ? { propertyId } : undefined)).data,
  changeovers: async (q: { propertyId?: string; phase?: string; includeDone?: boolean } = {}) => {
    const r = await api.get<Changeover[]>('/changeovers', { propertyId: q.propertyId, phase: q.phase, includeDone: q.includeDone ? 'true' : undefined });
    return { rows: r.data, counts: (r.meta as unknown as { counts: ChangeoverCounts }).counts };
  },
  setTarget: async (unitId: string, body: { targetReletDate?: string | null; note?: string | null }) => (await api.patch(`/changeovers/${unitId}`, body)).data,
};

export const EFFECT_NOTE: Partial<Record<ItemEffect, string>> = {
  ACTIVATE_TENANCY: 'Activates the tenancy',
  END_TENANCY: 'Ends the tenancy',
  BOOK_INSPECTION: 'Books an inspection',
  CLEANING_WORK_ORDER: 'Raises a cleaning job',
  WORKS_WORK_ORDER: 'Raises a work order',
  EJARI_NUMBER: 'Records the Ejari number',
  ALLOCATE_PARKING: 'Allocates the bay on the car park',
  CONFIRM_ID_GOODLORD: 'Tells Goodlord',
};

/** Where a ref opens. An inspection lives on its development's Property management → Visits. */
export function refUrl(ref: ChecklistItem['ref'], propertyId: string): string | null {
  if (!ref) return null;
  if (ref.type === 'workOrder') return `/app/work-orders/${ref.id}`;
  if (ref.type === 'inspection') return `/app/properties/${propertyId}?tab=management&sub=inspections`;
  if (ref.type === 'tenancy') return `/app/tenancies/${ref.id}`;
  return null; // a document opens through openStoredFile (it needs the session token), not a route
}

/** A document ref's file (GET /api/v1/files/:id). */
export function refFileUrl(ref: ChecklistItem['ref']): string | null {
  return ref?.type === 'document' ? `/api/v1/files/${ref.id}` : null;
}
